import type {
  CompetenceRecord,
  CompetenceStep,
  CompetenceValidation,
} from "../api/client";

/**
 * The auditor's competence record, client side.
 *
 * The same shape the certification body's `application.ts` has, and for the same reason:
 * the whole record is submitted as one payload, so the part-completed record is client
 * state until the last step. The server owns whether it is valid; this module owns only
 * what has been typed.
 */

export interface RecordStep {
  /**
   * The server's step keys this stop is answerable for — usually one, and two for
   * competency.
   *
   * **The rail's stops and the validator's steps are not the same list, by design.** The
   * server owns whether a record is complete and it reasons in the units the data
   * dictionary uses; the rail is how a person is walked through it. Merging two stops does
   * not merge two validations, and it must not: `qualifications` and `competence` stay
   * separate findings against separate fields, and this stop is complete when both are.
   */
  keys: readonly CompetenceStep[];
  title: string;
  /** Screen number(s) in the walkthrough, for orientation. */
  screen: string;
}

/**
 * Five stops over the prototype's six screens — 12 to 17.
 *
 * **Screens 13 and 14 are one stop.** Both were headed with a certification: screen 13 asks
 * for third-party auditor certification — scheme, grade, certificate number, expiry — and
 * screen 14 asks what that certification is *held for*, standard by sector. A reader who
 * had just entered "IRCA, Certified Lead Auditor, ISO 9001" on one screen was asked on the
 * next whether they were competent in ISO 9001, which reads as the same question twice and
 * invites two different answers to it. They are the two halves of one claim — the evidence
 * and its scope — and Cl. 7.1.2 reads them together, so they are entered together.
 *
 * The *validation* did not merge and the payload did not change: the server still answers
 * `qualifications` and `competence` separately, and a blocking finding still names the
 * field it came from. What changed is where a person is standing when they answer.
 */
export const RECORD_STEPS: readonly RecordStep[] = [
  { keys: ["identity"], title: "Identity", screen: "12" },
  { keys: ["qualifications", "competence"], title: "Competency", screen: "13–14" },
  { keys: ["experience"], title: "Experience", screen: "15" },
  { keys: ["declarations"], title: "Declarations", screen: "16" },
  { keys: ["sign"], title: "Sign", screen: "17" },
];

/**
 * Nothing pre-filled, and in particular no pre-ticked acknowledgement.
 *
 * The prototype opens screens 16 and 17 with every box already checked. An undertaking
 * nobody gave is not the same fact as one given, and the confidentiality and impartiality
 * declarations are the two this platform refuses a record without.
 */
export const EMPTY_RECORD: CompetenceRecord = {
  name: "",
  email: "",
  engagement: "",
  position: "",
  timezone: "",
  education: "",
  certifications: [],
  competence: [],
  ms_years: "",
  sector_years: "",
  audits_led: "",
  audit_days: "",
  witnessed_on: "",
  witnessed_by: "",
  training: [],
  confidentiality: null,
  impartiality: null,
  exclusions: [],
  ai_accountability: null,
  complao_training: null,
  signature: "",
};

export function resultFor(validation: CompetenceValidation | null, step: CompetenceStep | null) {
  if (!validation || !step) return null;
  return validation.steps.find((entry) => entry.step === step) ?? null;
}

/**
 * Every server result behind one stop on the rail.
 *
 * Ordered as the stop declares them, so the advisories under a merged stop read in the
 * order the fields above them were filled in.
 */
export function resultsFor(
  validation: CompetenceValidation | null,
  step: RecordStep | undefined,
) {
  if (!validation || !step) return [];
  return step.keys
    .map((key) => resultFor(validation, key))
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
}

/**
 * A stop is complete when every server step behind it is.
 *
 * `every` over an empty list is `true`, so the absent validation is caught first: a rail
 * cannot report a stop complete on the strength of never having asked.
 */
export function stepComplete(
  validation: CompetenceValidation | null,
  step: RecordStep | undefined,
): boolean {
  if (!validation || !step) return false;
  const results = resultsFor(validation, step);
  return results.length === step.keys.length && results.every((entry) => entry.complete);
}

export function blockingCount(
  validation: CompetenceValidation | null,
  step: RecordStep | undefined,
): number {
  return resultsFor(validation, step).reduce(
    (total, entry) => total + entry.blocking.length,
    0,
  );
}

/**
 * The blocking finding against one field, if there is one.
 *
 * Searched across every step behind the stop. The fields are distinct — `education` and
 * `certifications.0.expiry` come from `qualifications`, `competence.0.sectors` from
 * `competence` — so the first match is the only match, and the caller does not have to know
 * which half of a merged stop its field came from.
 */
export function blockingFor(
  validation: CompetenceValidation | null,
  step: RecordStep | undefined,
  field: string,
): string | undefined {
  for (const entry of resultsFor(validation, step)) {
    const issue = entry.blocking.find((finding) => finding.field === field);
    if (issue) return issue.detail;
  }
  return undefined;
}

/** One competence claim, by standard. */
export function claimFor(record: CompetenceRecord, standard: string) {
  return (record.competence ?? []).find((entry) => entry.standard === standard) ?? null;
}

export function claims(record: CompetenceRecord) {
  return record.competence ?? [];
}

/**
 * Turn a standard on or off in the claim.
 *
 * Turning it off removes its sectors, its level and its dates with it. Keeping them would
 * leave a claim the auditor has withdrawn sitting in the payload, and the server writes
 * what it is given.
 */
export function toggleClaim(record: CompetenceRecord, standard: string): CompetenceRecord {
  const existing = claimFor(record, standard);
  if (existing) {
    return {
      ...record,
      competence: claims(record).filter((entry) => entry.standard !== standard),
    };
  }
  return {
    ...record,
    competence: [
      ...claims(record),
      { standard, level: "", sectors: [], granted_on: "", next_review: "" },
    ],
  };
}

export function setClaim(
  record: CompetenceRecord,
  standard: string,
  patch: Partial<NonNullable<CompetenceRecord["competence"]>[number]>,
): CompetenceRecord {
  return {
    ...record,
    competence: claims(record).map((entry) =>
      entry.standard === standard ? { ...entry, ...patch } : entry,
    ),
  };
}

/** Replace the whole sector list under one standard — the multi-select's bulk actions. */
export function setClaimSectors(
  record: CompetenceRecord,
  standard: string,
  sectors: readonly number[],
): CompetenceRecord {
  return {
    ...record,
    competence: claims(record).map((entry) =>
      entry.standard === standard
        ? { ...entry, sectors: [...sectors].sort((a, b) => a - b) }
        : entry,
    ),
  };
}

/** Add or remove one IAF sector under one standard. Sectors stay in ascending order. */
export function toggleClaimSector(
  record: CompetenceRecord,
  standard: string,
  sector: number,
): CompetenceRecord {
  return {
    ...record,
    competence: claims(record).map((entry) => {
      if (entry.standard !== standard) return entry;
      const sectors = entry.sectors ?? [];
      return {
        ...entry,
        sectors: sectors.includes(sector)
          ? sectors.filter((index) => index !== sector)
          : [...sectors, sector].sort((a, b) => a - b),
      };
    }),
  };
}

/**
 * Add, patch and remove for the three repeatable blocks.
 *
 * Written once over a key rather than three times, because the three behave identically:
 * an empty row is a row the validator refuses, so "Add" gives a blank one and "Remove"
 * takes it away again.
 */
type ListKey = "certifications" | "training" | "exclusions";

export function addRow(record: CompetenceRecord, key: ListKey): CompetenceRecord {
  const blank = {
    certifications: { scheme: "", grade: "", certificate_number: "", expiry: "" },
    training: { course: "", provider: "", completed_on: "" },
    exclusions: { organisation_name: "", reason: "", excluded_until: "" },
  }[key];
  return { ...record, [key]: [...(record[key] ?? []), blank] };
}

export function setRow(
  record: CompetenceRecord,
  key: ListKey,
  index: number,
  patch: Record<string, string>,
): CompetenceRecord {
  return {
    ...record,
    [key]: (record[key] ?? []).map((row, at) => (at === index ? { ...row, ...patch } : row)),
  };
}

export function removeRow(
  record: CompetenceRecord,
  key: ListKey,
  index: number,
): CompetenceRecord {
  return { ...record, [key]: (record[key] ?? []).filter((_, at) => at !== index) };
}
